import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Assessment as ReportIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import AccountService from '../../services/account.service';
import JournalEntryService from '../../services/journal-entry.service';

const FinancialReports: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Determine active tab based on URL path
  const getInitialTab = () => {
    const path = location.pathname;
    if (path.includes('balance-sheet')) return 0;
    if (path.includes('income-statement')) return 1;
    if (path.includes('trial-balance')) return 2;
    return 0;
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [accountTypeFilter, setAccountTypeFilter] = useState('all');
  const [sortField, setSortField] = useState<string>('accountNumber');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    fetchAccounts();
  }, []);

  // Update active tab when URL changes
  useEffect(() => {
    setActiveTab(getInitialTab());
  }, [location.pathname]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await AccountService.getAll({
        'filters[isActive][$eq]': true,
        'pagination[pageSize]': 1000,
        sort: `${sortField}:${sortOrder}`,
      });
      setAccounts(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return 'OMR 0.00';
    return `OMR ${amount.toFixed(2)}`;
  };

  const getAccountsByType = (type: string) => {
    const filtered = accounts.filter((account) => account.attributes?.accountType === type);
    // Apply additional sorting based on user selection
    return filtered.sort((a, b) => {
      let aValue = a.attributes?.[sortField];
      let bValue = b.attributes?.[sortField];
      
      if (sortField === 'balance' || sortField === 'accountNumber') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const getTotalByType = (type: string) => {
    return getAccountsByType(type).reduce((sum, account) => sum + (account.attributes?.balance || 0), 0);
  };

  // Balance Sheet Report
  const BalanceSheet = () => {
    const assets = getTotalByType('asset');
    const liabilities = getTotalByType('liability');
    const equity = getTotalByType('equity');

    return (
      <Box>
        <Typography variant="h5" gutterBottom align="center">
          Balance Sheet
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" mb={3}>
          As of {new Date(endDate).toLocaleDateString()}
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Account</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Assets */}
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography variant="h6" color="primary">ASSETS</Typography>
                </TableCell>
              </TableRow>
              {getAccountsByType('asset').map((account) => (
                <TableRow key={account.id}>
                  <TableCell sx={{ pl: 4 }}>
                    {account.attributes?.accountNumber} - {account.attributes?.accountName}
                  </TableCell>
                  <TableCell align="right">{formatCurrency(account.attributes?.balance)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ pl: 4 }} ><strong>Total Assets</strong></TableCell>
                <TableCell align="right"><strong>{formatCurrency(assets)}</strong></TableCell>
              </TableRow>

              {/* Liabilities */}
              <TableRow>
                <TableCell colSpan={2} sx={{ pt: 3 }}>
                  <Typography variant="h6" color="primary">LIABILITIES</Typography>
                </TableCell>
              </TableRow>
              {getAccountsByType('liability').map((account) => (
                <TableRow key={account.id}>
                  <TableCell sx={{ pl: 4 }}>
                    {account.attributes?.accountNumber} - {account.attributes?.accountName}
                  </TableCell>
                  <TableCell align="right">{formatCurrency(account.attributes?.balance)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ pl: 4 }}><strong>Total Liabilities</strong></TableCell>
                <TableCell align="right"><strong>{formatCurrency(liabilities)}</strong></TableCell>
              </TableRow>

              {/* Equity */}
              <TableRow>
                <TableCell colSpan={2} sx={{ pt: 3 }}>
                  <Typography variant="h6" color="primary">EQUITY</Typography>
                </TableCell>
              </TableRow>
              {getAccountsByType('equity').map((account) => (
                <TableRow key={account.id}>
                  <TableCell sx={{ pl: 4 }}>
                    {account.attributes?.accountNumber} - {account.attributes?.accountName}
                  </TableCell>
                  <TableCell align="right">{formatCurrency(account.attributes?.balance)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ pl: 4 }}><strong>Total Equity</strong></TableCell>
                <TableCell align="right"><strong>{formatCurrency(equity)}</strong></TableCell>
              </TableRow>

              {/* Total */}
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>TOTAL LIABILITIES & EQUITY</strong></TableCell>
                <TableCell align="right"><strong>{formatCurrency(liabilities + equity)}</strong></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // Income Statement Report
  const IncomeStatement = () => {
    const revenue = getTotalByType('revenue');
    const expenses = getTotalByType('expense');
    const netIncome = revenue - expenses;

    return (
      <Box>
        <Typography variant="h5" gutterBottom align="center">
          Income Statement
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" mb={3}>
          For the period {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()}
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Account</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Revenue */}
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography variant="h6" color="primary">REVENUE</Typography>
                </TableCell>
              </TableRow>
              {getAccountsByType('revenue').map((account) => (
                <TableRow key={account.id}>
                  <TableCell sx={{ pl: 4 }}>
                    {account.attributes?.accountNumber} - {account.attributes?.accountName}
                  </TableCell>
                  <TableCell align="right">{formatCurrency(account.attributes?.balance)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ pl: 4 }}><strong>Total Revenue</strong></TableCell>
                <TableCell align="right"><strong>{formatCurrency(revenue)}</strong></TableCell>
              </TableRow>

              {/* Expenses */}
              <TableRow>
                <TableCell colSpan={2} sx={{ pt: 3 }}>
                  <Typography variant="h6" color="primary">EXPENSES</Typography>
                </TableCell>
              </TableRow>
              {getAccountsByType('expense').map((account) => (
                <TableRow key={account.id}>
                  <TableCell sx={{ pl: 4 }}>
                    {account.attributes?.accountNumber} - {account.attributes?.accountName}
                  </TableCell>
                  <TableCell align="right">{formatCurrency(account.attributes?.balance)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ pl: 4 }}><strong>Total Expenses</strong></TableCell>
                <TableCell align="right"><strong>{formatCurrency(expenses)}</strong></TableCell>
              </TableRow>

              {/* Net Income */}
              <TableRow sx={{ bgcolor: netIncome >= 0 ? 'success.light' : 'error.light' }}>
                <TableCell><strong>NET INCOME</strong></TableCell>
                <TableCell align="right">
                  <strong style={{ color: netIncome >= 0 ? 'green' : 'red' }}>
                    {formatCurrency(netIncome)}
                  </strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // Trial Balance Report
  const TrialBalance = () => {
    const totalDebits = accounts.reduce((sum, account) => {
      const balance = account.attributes?.balance || 0;
      return sum + (balance > 0 ? balance : 0);
    }, 0);

    const totalCredits = accounts.reduce((sum, account) => {
      const balance = account.attributes?.balance || 0;
      return sum + (balance < 0 ? Math.abs(balance) : 0);
    }, 0);

    return (
      <Box>
        <Typography variant="h5" gutterBottom align="center">
          Trial Balance
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" mb={3}>
          As of {new Date(endDate).toLocaleDateString()}
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Account #</TableCell>
                <TableCell>Account Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Debit</TableCell>
                <TableCell align="right">Credit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map((account) => {
                const attrs = account.attributes;
                const balance = attrs?.balance || 0;
                const debit = balance > 0 ? balance : 0;
                const credit = balance < 0 ? Math.abs(balance) : 0;

                return (
                  <TableRow key={account.id}>
                    <TableCell>{attrs?.accountNumber}</TableCell>
                    <TableCell>{attrs?.accountName}</TableCell>
                    <TableCell>{attrs?.accountType}</TableCell>
                    <TableCell align="right">{debit > 0 ? formatCurrency(debit) : '-'}</TableCell>
                    <TableCell align="right">{credit > 0 ? formatCurrency(credit) : '-'}</TableCell>
                  </TableRow>
                );
              })}
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell colSpan={3}><strong>TOTALS</strong></TableCell>
                <TableCell align="right"><strong>{formatCurrency(totalDebits)}</strong></TableCell>
                <TableCell align="right"><strong>{formatCurrency(totalCredits)}</strong></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <ReportIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Financial Reports
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>
            Print
          </Button>
          <Button variant="outlined" startIcon={<DownloadIcon />}>
            Export
          </Button>
        </Stack>
      </Stack>

      {/* Date Range Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" mb={2}>
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 200 }}
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 200 }}
          />
          <Button variant="contained" onClick={fetchAccounts}>
            Generate Report
          </Button>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={`${sortField}:${sortOrder}`}
              label="Sort By"
              onChange={(e) => {
                const [field, order] = e.target.value.split(':');
                setSortField(field);
                setSortOrder(order as 'asc' | 'desc');
                fetchAccounts();
              }}
            >
              <MenuItem value="accountNumber:asc">Account # (Ascending)</MenuItem>
              <MenuItem value="accountNumber:desc">Account # (Descending)</MenuItem>
              <MenuItem value="accountName:asc">Account Name (A-Z)</MenuItem>
              <MenuItem value="accountName:desc">Account Name (Z-A)</MenuItem>
              <MenuItem value="balance:desc">Balance (High to Low)</MenuItem>
              <MenuItem value="balance:asc">Balance (Low to High)</MenuItem>
              <MenuItem value="accountType:asc">Type (A-Z)</MenuItem>
              <MenuItem value="accountType:desc">Type (Z-A)</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Report Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, v) => {
            setActiveTab(v);
            const paths = [
              '/financial-reports/balance-sheet',
              '/financial-reports/income-statement',
              '/financial-reports/trial-balance'
            ];
            navigate(paths[v]);
          }} 
          variant="fullWidth"
        >
          <Tab label="Balance Sheet" />
          <Tab label="Income Statement" />
          <Tab label="Trial Balance" />
        </Tabs>
      </Paper>

      {/* Report Content */}
      <Paper sx={{ p: 3 }}>
        {loading ? (
          <Typography align="center">Loading...</Typography>
        ) : (
          <>
            {activeTab === 0 && <BalanceSheet />}
            {activeTab === 1 && <IncomeStatement />}
            {activeTab === 2 && <TrialBalance />}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default FinancialReports;

